/**
 * Typed topic constants for robot websocket communication.
 * Source of truth: interface_1/param/rogo_app_bridge.yml in rogoag/navigation
 */

// Outbound: topics sent FROM the app TO the robot
export const OUTBOUND_TOPICS = {
  // mission
  MISSION_CUD_SYNC: 'mission/cud_sync',
  MISSION_SET_WAYPOINT: 'mission/set_waypoint',

  // depth & coring
  CONFIG_SET_DEPTH: 'config/set_depth',
  CONFIG_SET_DEPTH_OFFSET: 'config/set_depth_offset',
  CONFIG_SET_OVERRIDE_DEPTH: 'config/set_override_depth',
  CONFIG_SET_DEPTH_ARRIVAL_TARGET: 'config/set_depth_arrival_target',
  CONFIG_SET_CORE_TIMEOUT: 'config/set_core_timeout',
  CONFIG_SET_TIP_TO_GROUND: 'config/set_tip_to_ground',
  CONFIG_SET_BIG_MOTOR: 'config/set_big_motor',
  APP_CORE_ALLOW: 'app/core_allow',

  // speed & navigation
  CONFIG_SET_MAX_SPEED: 'config/set_max_speed',
  CONFIG_SET_ROTATION_SPEED: 'config/set_rotation_speed',
  CONFIG_SET_CARRIAGE_DOWN_SPEED: 'config/set_carriage_down_speed',
  CONFIG_SET_CARRIAGE_UP_SPEED: 'config/set_carriage_up_speed',
  CONFIG_SET_DWELL_TIME: 'config/set_dwell_time',
  CONFIG_SET_DWELL_AT_TOP_TIME: 'config/set_dwell_at_top_time',

  // safety
  CONFIG_SET_BRAKE_SAFETY_CHECK: 'config/set_brake_safety_check',
  CONFIG_SET_SPEED_SAFETY_CHECK: 'config/set_speed_safety_check',

  // throttle & brake tuning
  CONFIG_SET_TH_DEADBAND: 'config/set_th_deadband',
  CONFIG_SET_BR_DEADBAND: 'config/set_br_deadband',
  CONFIG_SET_TH_MAX_POS: 'config/set_th_max_pos',
  CONFIG_SET_TH_MIN_POS: 'config/set_th_min_pos',
  CONFIG_SET_BR_MAX_POS: 'config/set_br_max_pos',
  CONFIG_SET_BR_MIN_POS: 'config/set_br_min_pos',
  CONFIG_SET_ST_MAX_PWM: 'config/set_st_max_pwm',
  CONFIG_SET_ST_MIN_PWM: 'config/set_st_min_pwm',

  // current limits
  CONFIG_SET_DCC_MAX: 'config/set_dcc_max',
  CONFIG_SET_DCC_MIN: 'config/set_dcc_min',
  CONFIG_SET_DCW_MAX: 'config/set_dcw_max',
  CONFIG_SET_DCW_MIN: 'config/set_dcw_min',
  CONFIG_SET_CDN_MAX: 'config/set_cdn_max',
  CONFIG_SET_CDN_MIN: 'config/set_cdn_min',
  CONFIG_SET_CUP_MAX: 'config/set_cup_max',
  CONFIG_SET_CUP_MIN: 'config/set_cup_min',

  // HMI & UI
  CONFIG_SET_IMPLEMENT_SELECTED: 'config/set_implement_selected',
  CONFIG_SET_SEND_VIS_SENSORS: 'config/set_send_vis_sensors',
  APP_BARCODE_COMPLETED: 'app/barcode/completed',
} as const;

export type OutboundTopic = (typeof OUTBOUND_TOPICS)[keyof typeof OUTBOUND_TOPICS];

// Dynamic HMI publish topics
export const hmiOutboundTopic = (buttonID: string) => `hmi/${buttonID}/pressed` as const;

// Inbound: topics received FROM the robot BY the app
export const INBOUND_TOPICS = {
  // position & navigation
  POSITION: 'position',
  NAVIGATION_WAYPOINTS: 'navigation/waypoints',
  NAVIGATION_NAV_LEG_COMPLETE: 'navigation/nav_leg_complete',
  MISSION_WAYPOINT: 'mission/waypoint',
  MISSION_WAYPOINT_RESET: 'mission/waypoint_reset',
  MISSION_SET_WAYPOINT: 'mission/set_waypoint',

  // mission sync
  MISSION_SYNC: 'mission/sync',
  MISSION_CUD_SYNC: 'mission/cud_sync',
  MISSION_SYNC_DONE: 'mission/sync_done',

  // arm completions
  ARM_CORE_COMPLETE: 'arm/core_complete',
  ARM_DUMP_COMPLETE: 'arm/dump_complete',
  ARM_HOME_COMPLETE: 'arm/home_complete',

  // config read-back
  CONFIG_DEPTH: 'config/depth',
  CONFIG_DEPTH_OFFSET: 'config/depth_offset',
  CONFIG_OVERRIDE_DEPTH: 'config/override_depth',
  CONFIG_CORE_TIMEOUT: 'config/core_timeout',
  CONFIG_MAX_SPEED: 'config/max_speed',
  CONFIG_TIP_TO_GROUND: 'config/tip_to_ground',
  CONFIG_BIG_MOTOR: 'config/big_motor',
  CONFIG_IMPLEMENT_SELECTED: 'config/implement_selected',
  CONFIG_ROTATION_SPEED_SETTING: 'config/rotation_speed_setting',
  CONFIG_CARRIAGE_DOWN_SPEED: 'config/carriage_down_speed',
  CONFIG_CARRIAGE_UP_SPEED: 'config/carriage_up_speed',
  CONFIG_DWELL_TIME: 'config/dwell_time',
  CONFIG_DWELL_AT_TOP_TIME: 'config/dwell_at_top_time',
  CONFIG_DCC_MAX: 'config/dcc_max',
  CONFIG_DCC_MIN: 'config/dcc_min',
  CONFIG_DCW_MAX: 'config/dcw_max',
  CONFIG_DCW_MIN: 'config/dcw_min',
  CONFIG_CDN_MAX: 'config/cdn_max',
  CONFIG_CDN_MIN: 'config/cdn_min',
  CONFIG_CUP_MAX: 'config/cup_max',
  CONFIG_CUP_MIN: 'config/cup_min',
  CONFIG_BRAKE_SAFETY_CHECK: 'config/brake_safety_check',
  CONFIG_SPEED_SAFETY_CHECK: 'config/speed_safety_check',
  CONFIG_DEPTH_ARRIVAL_TARGET: 'config/depth_arrival_target',
  CONFIG_TH_DEADBAND: 'config/th_deadband',
  CONFIG_BR_DEADBAND: 'config/br_deadband',
  CONFIG_TH_MAX_POS: 'config/th_max_pos',
  CONFIG_TH_MIN_POS: 'config/th_min_pos',
  CONFIG_BR_MAX_POS: 'config/br_max_pos',
  CONFIG_BR_MIN_POS: 'config/br_min_pos',
  CONFIG_ST_MAX_PWM: 'config/st_max_pwm',
  CONFIG_ST_MIN_PWM: 'config/st_min_pwm',
  CONFIG_TH_POSITION: 'config/th_position',
  CONFIG_BR_POSITION: 'config/br_position',
  CONFIG_CARRIAGE_POSITION: 'config/carriage_position',
  CONFIG_SEND_VIS_SENSORS: 'config/send_vis_sensors',
  CONFIG_OFFSET_X: 'config/offset_x',
  CONFIG_OFFSET_Y: 'config/offset_y',

  // robot status
  ROBOT_STATE: 'robot_state',
  ROBOT_NAME: 'robot_name',
  STATUS_PHYSICAL_SOILINBUCKET: 'status/physical/soilinbucket',
  STATUS_LOGICAL_BULKING: 'status/logical/bulking',
  STATUS_LOGICAL_ARM_TYPE: 'status/logical/arm_type',

  // vis sensors
  VIS_SAFE_PROX_SENSOR: 'vis/safe_prox_sensor',
  VIS_DUMP_PROX_SENSOR: 'vis/dump_prox_sensor',
  VIS_DOOR_PROX_SENSOR: 'vis/door_prox_sensor',
  VIS_BUCKET_PROX_SENSOR: 'vis/bucket_prox_sensor',
  VIS_SEATBELT_SENSOR: 'vis/seatbelt_sensor',
  VIS_PRESSURE_ACTIVE_SENSOR: 'vis/pressure_active_sensor',
  VIS_ESTOP_CUT: 'vis/estop_cut',

  // scanner
  APP_BARCODE: 'app/barcode',

  // other
  CURRENT_JOB_SAMPLE: 'current_job_sample',
  MISSION_PREV_SAMPLE_ID: 'mission/prev/sample_id',
} as const;

export type InboundTopic = (typeof INBOUND_TOPICS)[keyof typeof INBOUND_TOPICS];

// Dynamic HMI subscribe topics
export const hmiInboundTopic = (buttonID: string, suffix: 'color' | 'label') => `hmi/${buttonID}/${suffix}` as const;

// Helper to build EventBus key for robot message subscriptions
export const rosEventKey = (topic: InboundTopic | `hmi/${string}/${'color' | 'label'}`) => `ROSMSG/${topic}` as const;
